import React from "react";
import {
  Box,
  Dialog,
  Stack,
  Typography,
  Paper,
  useMediaQuery,
  Button,
  Grid,
  Card,
  Chip,
  IconButton,
  Select,
  MenuItem,
} from "@mui/material";
import { useMemo, useState, useEffect } from "react";
import { useTheme } from "@emotion/react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { BackArrow } from "../../assets";
import { Wrapper } from "../wrapper";
import { CustomTable } from "../table";
import { DataColumns } from "./constant";
import { DataColumns as DashboardColumns } from "../../pages/dashboard/constants";
import { Modal } from "../modal";
import {
  dashboardCharts,
  apiCategortList,
  usageLogs,
  downloadLogs,
  averageResponseTime,
} from "../../services";
import { SelectDropdown, CustomDivider, Calender, TableButton } from "../";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ApexPieChart from "../../charts/pie-charts/dashboard";
import DateTimeGraph from "../../charts/line-charts/dashboard";
import dayjs from "dayjs";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { CheckCircle } from "@mui/icons-material";

export const ViewLogs = () => {
  const location = useLocation();
  const { date, selectedApiId } = location.state || {};

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { user } = useSelector((state) => state.auth);
  const jwtToken = user?.jwt;

  const [dashboardChartsData, setDashboardChartsData] = useState({});
  const [apiCategoryAndList, setApiCategoryAndList] = useState({});
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedAPI, setSelectedAPI] = useState(selectedApiId || "");
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [isCalendarChanged, setIsCalendarChanged] = useState(false);
  const [dashboardUsageData, setDashboardUsageData] = useState([]);
  const [responseTimeData, setResponseTimeData] = useState({});

  const navigate = useNavigate();

  // Date range for charts
  const currentDate = dayjs(date);
  const sevenDaysBack = currentDate.subtract(7, "day");
  const defaultDateRange = [
    sevenDaysBack.startOf("day"),
    currentDate.endOf("day"),
  ];
  const [dateRangeValue, setDateRangeValue] = useState(defaultDateRange);
  const [dashboardDates, setDashboardDates] = useState({
    startDate: defaultDateRange[0].format("YYYY-MM-DD"),
    endDate: defaultDateRange[1].format("YYYY-MM-DD"),
  });

  useEffect(() => {
    const getApiCategoryList = async () => {
      try {
        const res = await apiCategortList();
        if (res?.status === 200) {
          const categories = res?.data?.data?.attributes?.apisByCategory || {};
          setApiCategoryAndList(categories);
        }
      } catch (error) {
        console.error("Error fetching API categories:", error);
      }
    };
    getApiCategoryList();
  }, []);

  const fetchChartData = async () => {
    try {
      const res = await dashboardCharts(
        selectedCategory === "all" ? null : selectedAPI,
        dashboardDates
      );
      if (res?.status === 200) {
        setDashboardChartsData(res?.data);
      }
    } catch (error) {
      console.error("Error fetching dashboard charts data:", error);
    }
  };

  const fetchUsageData = async () => {
    try {
      const res = await usageLogs({
        token: jwtToken,
        startDate: dashboardDates?.startDate,
        endDate: dashboardDates?.endDate,
        apiId: selectedCategory === "all" ? null : selectedAPI,
      });
      if (res?.status === 200) {
        setDashboardUsageData(res?.data || []);
      }
    } catch (error) {
      console.error("Error fetching usage data:", error);
    }
  };

  const fetchResponseTimeData = async () => {
    try {
      const res = await averageResponseTime(
        dashboardDates?.startDate,
        dashboardDates?.endDate
      );
      if (res?.status === 200) {
        setResponseTimeData(res?.data || {});
      }
    } catch (error) {
      console.error("Error fetching response time data:", error);
    }
  };

  useEffect(() => {
    fetchChartData();
    fetchUsageData();
    fetchResponseTimeData();
  }, [selectedAPI, selectedCategory, dashboardDates]);

  useEffect(() => {
    setIsFilterActive(selectedCategory !== "all" || isCalendarChanged);
    if (
      selectedCategory !== "all" &&
      apiCategoryAndList[selectedCategory]?.apis
    ) {
      const sortedApis = apiCategoryAndList[selectedCategory]?.apis.sort(
        (a, b) => a.name.localeCompare(b.name)
      );
      setSelectedAPI(sortedApis[0]?.id || "");
    } else if (selectedCategory === "all") {
      setSelectedAPI("");
    }
  }, [selectedCategory, apiCategoryAndList, isCalendarChanged]);

  const { apiOptions } = useMemo(() => {
    const selected = apiCategoryAndList[selectedCategory] || [];
    if (selected?.apis?.length > 0) {
      const sortedApiOptions = selected?.apis
        .map((item) => ({
          label: item.name,
          value: item.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
      return { apiOptions: sortedApiOptions };
    }
    return { apiOptions: [] };
  }, [selectedCategory, apiCategoryAndList]);

  const handleChangeCalender = (dates) => {
    if (dates) {
      setDashboardDates({
        startDate: dates[0].format("YYYY-MM-DD"),
        endDate: dates[1].format("YYYY-MM-DD"),
      });
      setDateRangeValue(dates);
      setIsCalendarChanged(true);
    } else {
      setDashboardDates({
        startDate: defaultDateRange[0].format("YYYY-MM-DD"),
        endDate: defaultDateRange[1].format("YYYY-MM-DD"),
      });
      setDateRangeValue(defaultDateRange);
      setIsCalendarChanged(true);
    }
  };

  const handleResetFilter = () => {
    setSelectedCategory("all");
    setSelectedAPI("");
    setDashboardDates({
      startDate: defaultDateRange[0].format("YYYY-MM-DD"),
      endDate: defaultDateRange[1].format("YYYY-MM-DD"),
    });
    setDateRangeValue(defaultDateRange);
    setIsCalendarChanged(false);
  };

  const handleDownloadLogs = async (id) => {
    try {
      const response = await downloadLogs(
        selectedCategory === "all" ? null : id,
        dashboardDates
      );
      if (response?.data) {
        const url = URL.createObjectURL(response?.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = "logs.xlsx";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const stats = [
    {
      title: "Total API Calls",
      value: dashboardChartsData?.pieChart?.totalApiConsumption?.toLocaleString() || "6,464",
      change: dashboardChartsData?.pieChart?.totalApiConsumptionChange 
        ? `${dashboardChartsData.pieChart.totalApiConsumptionChange > 0 ? '+' : ''}${dashboardChartsData.pieChart.totalApiConsumptionChange}% from last period`
        : "+14% from last period",
      icon: (
        <ShowChartIcon
          sx={{ width: "2.5rem", height: "2.5rem", color: "#65758B" }}
        />
      ),
    },
    {
      title: "Success Rate",
      value: dashboardChartsData?.pieChart?.successRate 
        ? `${dashboardChartsData.pieChart.successRate}%`
        : "97.9%",
      change: dashboardChartsData?.pieChart?.successRateChange
        ? `${dashboardChartsData.pieChart.successRateChange > 0 ? '+' : ''}${dashboardChartsData.pieChart.successRateChange}% from last period`
        : "+0.3% from last period",
        icon: (
          <CheckCircle
            sx={{ width: "2.5rem", height: "2.5rem", color: "#65758B" }}
          />
        ),
    },
    {
      title: "Credits Consumed",
      value: responseTimeData?.consumedCredits?.toLocaleString() || "1,249",
      change: responseTimeData?.consumedCreditsChange !== "N/A" 
        ? `${responseTimeData?.consumedCreditsChange > 0 ? '+' : ''}${responseTimeData?.consumedCreditsChange}% from last period`
        : "+11% from last period",
      icon: (
        <CalendarMonthIcon
          sx={{ width: "2.5rem", height: "2.5rem", color: "#65758B" }}
        />
      ),
    },
    {
      title: "Avg Response Time",
      value: responseTimeData?.averageResponseTime 
        ? `${responseTimeData.averageResponseTime}s`
        : "1.4s",
      change: responseTimeData?.averageResponseTimeChange
        ? `${responseTimeData.averageResponseTimeChange > 0 ? '+' : ''}${responseTimeData.averageResponseTimeChange}s from last period`
        : "-0.2s from last period",
      badge: responseTimeData?.averageResponseTime 
        ? `${responseTimeData.averageResponseTime}s`
        : "1.4s",
    },
  ];

  const [days, setDays] = React.useState("7");


  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarKey, setCalendarKey] = useState(0);
  const handleDaysChange = (value) => {
    setDays(value);

    if (value === "custom") {
      // force re-mount
      setCalendarKey((prev) => prev + 1);
      setCalendarOpen(true);
      return;
    }

    setCalendarOpen(false);

    // 7 / 30 / 90 days logic
    const endDate = dayjs();
    const startDate = endDate.subtract(Number(value), "day");

    const newRange = [startDate.startOf("day"), endDate.endOf("day")];
    setDateRangeValue(newRange);
    setDashboardDates({
      startDate: newRange[0].format("YYYY-MM-DD"),
      endDate: newRange[1].format("YYYY-MM-DD"),
    });
    setIsCalendarChanged(true);
  };

  return (
    <Box>
      <Wrapper
        sx={{ paddingTop: theme.spacing(8), marginTop: { xs: "30px", md: 0 } }}
      >
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.textColor?.bluishgray || "#65758B",
                  fontWeight: 400,
                  fontSize: "16px",
                }}
              >
                Detailed analytics and insights for your API usage
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box
              sx={{
                display: "flex",
                justifyContent: { xs: "flex-start", md: "flex-end" },
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Box display="flex" flexDirection="row" gap={1}>
                <Select
                  value={days}
                  onChange={(e) => handleDaysChange(e.target.value)}
                  size="small"
                  sx={{
                    borderRadius: "8px",
                    fontSize: "14px",
                    height: "36px",
                    bgcolor: "background.paper",
                    boxShadow: 1,
                    "& .MuiSelect-select": {
                      display: "flex",
                      alignItems: "center",
                    },
                  }}
                >
                  <MenuItem value="7">Last 7 days</MenuItem>
                  <MenuItem value="30">Last 30 days</MenuItem>
                  <MenuItem value="90">Last 90 days</MenuItem>
                  <MenuItem value="custom">Custom Range</MenuItem>
                </Select>

                {/* Calendar opens inline below the select */}
                {calendarOpen && (
                  <Box>
                    <Calender
                      handleChange={(dates) => {
                        if (dates) {
                          handleChangeCalender(dates);
                        }
                      }}
                      defaultValue={defaultDateRange}
                      value={dateRangeValue}
                      maxDate={new Date()}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
        <Grid container spacing={2} mt={1}>
          {stats.map((stat, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  height: "100%",
                  boxShadow: "none",
                  border: "1px solid #E5EAF2",
                  padding: "20px",
                }}
              >
                <Box>
                  {/* Title + Optional Icon */}
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={1}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.textColor.verydarkblue,
                        fontWeight: 400,
                        fontSize: "16px",
                      }}
                    >
                      {stat.title}
                    </Typography>
                    {stat.icon && (
                      <IconButton sx={{ padding: "0" }}>{stat.icon}</IconButton>
                    )}
                    {stat.badge && (
                      <Chip
                        label={stat.badge}
                        size="small"
                        sx={{
                          backgroundColor:
                            stat.badge === "Good" ? "#DFF7E8" : "#F3F4F6",
                          color: stat.badge === "Good" ? "#1A7F37" : "#111",
                          fontWeight: 500,
                          height: "22px",
                        }}
                      />
                    )}
                  </Box>

                  {/* Main Value + Optional Badge */}
                  <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <Typography
                      variant="h5"
                      sx={{
                        color: theme.palette.textColor.verydarkblue,
                        fontWeight: 700,
                        fontSize: "22px",
                      }}
                    >
                      {stat.value}
                    </Typography>
                  </Box>

                  {/* Subtext */}
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.textColor.bluishgray,
                      fontWeight: 400,
                      fontSize: "12px",
                    }}
                  >
                    {stat.change}
                  </Typography>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
        <Typography
          sx={{
            color: theme.palette.textColor?.verydarkblue || "#020817",
            fontWeight: 700,
            fontSize: "26px",
            mb: 3,
            mt: 3,
          }}
          variant="h4"
        >
          Usage Summary
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: "1rem",
            mb: 3,
          }}
        >
          <Stack
            spacing={1}
            sx={{ width: { xs: "100%", sm: "auto", md: "auto" } }}
          >
            <Typography
              variant="body1"
              sx={{ color: theme.palette.grey.darken4 }}
            >
              Type of API
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: "1.5rem" }}>
              <SelectDropdown
                menuOptions={[
                  { label: "All", value: "all" },
                  ...Object.keys(apiCategoryAndList)
                    .sort()
                    .map((category) => ({
                      label: category,
                      value: category,
                    })),
                ]}
                value={selectedCategory}
                onChange={(value) => setSelectedCategory(value)}
                widthSize={isSmallScreen ? "100%" : "22rem"}
              />
              <CustomDivider orientation="vertical" />
            </Box>
          </Stack>

          <Stack
            spacing={1}
            sx={{ width: { xs: "100%", sm: "auto", md: "auto" } }}
          >
            <Typography
              variant="body1"
              sx={{ color: theme.palette.grey.darken4 }}
            >
              APIs
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: "1.5rem" }}>
              <SelectDropdown
                menuOptions={apiOptions}
                value={selectedAPI}
                onChange={setSelectedAPI}
                widthSize={isSmallScreen ? "100%" : "22rem"}
                disabled={selectedCategory === "all"}
              />
              {/* <CustomDivider orientation="vertical" /> */}
            </Box>
          </Stack>
          {/* <Stack
            spacing={1}
            sx={{ width: { xs: "100%", sm: "auto", md: "auto" } }}
          >
            <Typography
              variant="body1"
              sx={{ color: theme.palette.grey.darken4 }}
            >
              Select date
            </Typography>
            <Calender
              handleChange={handleChangeCalender}
              defaultValue={defaultDateRange}
              value={dateRangeValue}
            />
          </Stack> */}

          {/* {isFilterActive && (
            <Button
              onClick={handleResetFilter}
              variant="contained"
              sx={{ alignSelf: "end", maxHeight: "32px" }}
            >
              Reset Filter
            </Button>
          )} */}
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: "1.5rem",
            flexDirection: isSmallScreen ? "column" : "row",
            mb: 4,
          }}
        >
          <Box sx={{ flex: 0.7 }}>
            <Paper
              sx={{
                height: "100%",
                borderRadius: "16px",
                boxShadow:
                  "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
                border: "1px solid #f1f5f9",
                p: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: "#1e293b",
                  mb: 2,
                  fontSize: "18px",
                }}
              >
                API Usage Trends
              </Typography>
              <DateTimeGraph data={dashboardChartsData?.areaChart} />
            </Paper>
          </Box>
          <Box sx={{ flex: 0.3 }}>
            <Paper
              sx={{
                height: "100%",
                borderRadius: "16px",
                boxShadow:
                  "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
                border: "1px solid #f1f5f9",
                display: "flex",
                flexDirection: "column",
                p: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: "#1e293b",
                  mb: 2,
                  fontSize: "18px",
                }}
              >
                Usage Distribution
              </Typography>
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ApexPieChart
                  piechartData={
                    dashboardChartsData.pieChart
                      ? dashboardChartsData.pieChart
                      : null
                  }
                />
              </Box>
            </Paper>
          </Box>
        </Box>

        <Box
          sx={{
            mt: 5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              flexWrap: "wrap",
              mb: 2,
            }}
          >
            <Typography
              sx={{
                color: theme.palette.textColor?.verydarkblue || "#020817",
                fontWeight: 700,
                fontSize: "26px",
              }}
              variant="h5"
            >
              Usage Logs
            </Typography>
            <Button
              onClick={() => handleDownloadLogs(selectedAPI)}
              variant="contained"
              disabled={
                dashboardUsageData.length === 0 ||
                dashboardUsageData.every((entry) => entry.apiUsed === 0)
              }
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              Download logs
            </Button>
          </Box>
          <CustomTable
            columns={DashboardColumns}
            data={dashboardUsageData.map((item, index) => ({
              ...item,
              usage: (
                <TableButton
                  key={index}
                  onClick={() => {
                    navigate("/detailed-logs", {
                      state: {
                        date: item?.date,
                        selectedApiId: selectedAPI,
                        selectedCategory: selectedCategory,
                      },
                    });
                  }}
                >
                  View Logs
                </TableButton>
              ),
              key: index,
            }))}
          />
        </Box>
      </Wrapper>
    </Box>
  );
};
